import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import {
    Car,
    Bus,
    AlertTriangle,
    MapPin,
    Clock,
    Calendar,
    User,
    Users,
    Phone,
    MessageSquare,
    CheckCircle,
    XCircle,
    Loader2,
    Filter,
    Search,
    ChevronDown,
    Navigation,
    UserCheck,
    ArrowRight,
    Repeat,
    BarChart3,
    Download,
    Building2,
    Route,
    ExternalLink,
    Copy,
    Check,
    Star,
    Truck,
    Link2,
    Unlink,
    UserPlus
} from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import Modal from './common/Modal';
import toast from 'react-hot-toast';
import { NumeroRodante } from './Proyectos/utils/motion';

// Una solicitud es la misma pieza suelta o dentro de un viaje compartido: al
// juntar viajes, cada tarjeta vuela a su fila de la burbuja; al separarla, la
// fila vuela afuera y vuelve a ser tarjeta. Solo se mueve la posicion (sin
// estirar el texto).
const VUELO = { type: 'spring', stiffness: 360, damping: 32 };
const idVuelo = (id) => `taxi-${id}`;

const FLOW_TABS = [
    { key: 'pending', label: 'Pendientes', color: 'amber' },
    { key: 'confirmed', label: 'Confirmadas', color: 'blue' },
];

const DATA_TABS = [
    { key: 'history', label: 'Historial', color: 'gray' },
    { key: 'reporte', label: 'Reporte', color: 'indigo' },
];

const STATUS_CONFIG = {
    pending_approval: { label: 'Esperando Aprobación', bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500' },
    pending: { label: 'Pendiente', bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
    confirmed: { label: 'Confirmada', bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
    completed: { label: 'Completada', bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    cancelled: { label: 'Cancelada', bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
    rejected: { label: 'Rechazada', bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
};

const MESES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const StarRating = ({ value, size = 14 }) => (
    <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
            <Star key={i} size={size} className={i <= value ? 'text-amber-400 fill-amber-400' : 'text-gray-200'} />
        ))}
    </div>
);

const ShareRouteButton = ({ url }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* ignore */ }
    };
    return (
        <button onClick={handleCopy}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${copied ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
            title="Copiar link de Google Maps para el taxista"
        >
            {copied ? <Check size={14} /> : <Navigation size={14} />}
            {copied ? 'Copiado' : 'Ruta'}
        </button>
    );
};

const ReportePanel = ({ mes, anio, onMesChange, onAnioChange, reporte, loading, exporting, onExport }) => {
    return (
        <div className="space-y-6">
            {/* Controles */}
            <div className="flex items-center gap-3 flex-wrap">
                <select
                    value={mes}
                    onChange={(e) => onMesChange(Number(e.target.value))}
                    className="px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500"
                >
                    {MESES.slice(1).map((m, i) => (
                        <option key={i + 1} value={i + 1}>{m}</option>
                    ))}
                </select>
                <select
                    value={anio}
                    onChange={(e) => onAnioChange(Number(e.target.value))}
                    className="px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500"
                >
                    {[2025, 2026, 2027].map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
                <button
                    onClick={onExport}
                    disabled={exporting || !reporte?.total_viajes}
                    className="flex items-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                    {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                    Exportar Excel
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 size={32} className="animate-spin text-gray-400" />
                </div>
            ) : !reporte ? null : reporte.total_viajes === 0 ? (
                <div className="text-center py-20">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <BarChart3 size={32} className="text-gray-300" />
                    </div>
                    <p className="text-gray-500 text-lg">Sin viajes completados en {MESES[mes]} {anio}</p>
                </div>
            ) : (
                <>
                    {/* Resumen */}
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                        <div className="bg-white rounded-2xl border border-gray-100 p-5">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                                    <Car size={20} className="text-indigo-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-gray-900">{reporte.total_viajes}</p>
                                    <p className="text-xs text-gray-500">Viajes totales</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl border border-gray-100 p-5">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                                    <Building2 size={20} className="text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-gray-900">{reporte.por_departamento?.length || 0}</p>
                                    <p className="text-xs text-gray-500">Departamentos</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl border border-gray-100 p-5">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                                    <Route size={20} className="text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-gray-900">{reporte.por_ruta?.length || 0}</p>
                                    <p className="text-xs text-gray-500">Rutas distintas</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl border border-gray-100 p-5">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                                    <Truck size={20} className="text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-gray-900">{reporte.por_proveedor?.length || 0}</p>
                                    <p className="text-xs text-gray-500">Proveedores</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tablas */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Por Departamento */}
                        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                            <div className="p-4 border-b border-gray-100">
                                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                    <Building2 size={16} className="text-purple-500" />
                                    Por Departamento
                                </h3>
                            </div>
                            <div className="divide-y divide-gray-50">
                                {reporte.por_departamento?.map((item, i) => (
                                    <div key={i} className="px-4 py-3 flex items-center justify-between">
                                        <span className="text-sm text-gray-700">{item.nombre}</span>
                                        <span className="text-sm font-semibold text-gray-900 bg-gray-100 px-2.5 py-0.5 rounded-full">{item.viajes}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Por Proveedor */}
                        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                            <div className="p-4 border-b border-gray-100">
                                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                    <Truck size={16} className="text-emerald-500" />
                                    Por Proveedor
                                </h3>
                            </div>
                            <div className="divide-y divide-gray-50">
                                {reporte.por_proveedor?.map((item, i) => (
                                    <div key={i} className="px-4 py-3 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm text-gray-700">{item.nombre}</span>
                                            {item.calificacion && (
                                                <div className="flex items-center gap-1">
                                                    <StarRating value={Math.round(item.calificacion)} size={12} />
                                                    <span className="text-xs text-gray-400">{item.calificacion}</span>
                                                </div>
                                            )}
                                        </div>
                                        <span className="text-sm font-semibold text-gray-900 bg-gray-100 px-2.5 py-0.5 rounded-full">{item.viajes}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Por Empleado */}
                        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                            <div className="p-4 border-b border-gray-100">
                                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                    <User size={16} className="text-blue-500" />
                                    Por Empleado
                                </h3>
                            </div>
                            <div className="divide-y divide-gray-50 max-h-[300px] overflow-y-auto">
                                {reporte.por_empleado?.map((item, i) => (
                                    <div key={i} className="px-4 py-3 flex items-center justify-between">
                                        <span className="text-sm text-gray-700">{item.nombre}</span>
                                        <span className="text-sm font-semibold text-gray-900 bg-gray-100 px-2.5 py-0.5 rounded-full">{item.viajes}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Por Ruta */}
                        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                            <div className="p-4 border-b border-gray-100">
                                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                    <Route size={16} className="text-amber-500" />
                                    Rutas más frecuentes
                                </h3>
                            </div>
                            <div className="divide-y divide-gray-50 max-h-[300px] overflow-y-auto">
                                {reporte.por_ruta?.slice(0, 15).map((item, i) => (
                                    <div key={i} className="px-4 py-3 flex items-center justify-between">
                                        <span className="text-sm text-gray-700">{item.ruta}</span>
                                        <span className="text-sm font-semibold text-gray-900 bg-gray-100 px-2.5 py-0.5 rounded-full">{item.viajes}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// Capacidad (pasajeros, contando a quien pide): carro hasta 4; van de 4 a 12.
const CAPACIDAD_CARRO = 4;
const CAPACIDAD_VAN = 12;
// Para ir en el mismo vehiculo, las horas de recogida pueden diferir hasta esto.
const VENTANA_RECOGIDA_MIN = 45;
// "18:00" -> "6:00 PM"
const hora12 = (hhmm) => {
    if (!hhmm) return '-';
    const [h, m] = hhmm.split(':').map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};
const minutos = (hhmm) => {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
};

const distanciaKm = (a, b) => {
    if (!a || !b) return null;
    const rad = (g) => (g * Math.PI) / 180;
    const dLat = rad(b[0] - a[0]);
    const dLng = rad(b[1] - a[1]);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(dLng / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const TaxiManagementView = () => {
    const [activeTab, setActiveTab] = useState('pending');
    const [solicitudes, setSolicitudes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterDate, setFilterDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [updating, setUpdating] = useState(null);

    // Modal state
    const [proveedores, setProveedores] = useState([]);
    const [assignModal, setAssignModal] = useState(null); // solicitud para asignar proveedor
    const [selectedProveedor, setSelectedProveedor] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null); // solicitud para confirmar con conductor
    const [conductorInput, setConductorInput] = useState('');
    const [conductorTelInput, setConductorTelInput] = useState('');
    const [conductorPlacaInput, setConductorPlacaInput] = useState(''); // modelo del vehiculo

    // Consolidación
    const [consolidando, setConsolidando] = useState(false);
    const [selectedForGroup, setSelectedForGroup] = useState([]);

    // Reporte
    const now = new Date();
    const [reporteMes, setReporteMes] = useState(now.getMonth() + 1);
    const [reporteAnio, setReporteAnio] = useState(now.getFullYear());
    const [reporte, setReporte] = useState(null);
    const [loadingReporte, setLoadingReporte] = useState(false);
    const [exportingExcel, setExportingExcel] = useState(false);
    const [mergeSugerencias, setMergeSugerencias] = useState([]);
    // Burbujas de viaje compartido desplegadas (por grupo_id): cerradas solo muestran lo basico.
    const [burbujasAbiertas, setBurbujasAbiertas] = useState([]);
    // Sumar personas a un viaje (compartido o suelto) sin depender de la pestaña:
    // { base, miembros } del viaje; candidatos del mismo dia, pendientes o confirmados.
    const [sumarModal, setSumarModal] = useState(null);
    const [candidatosSumar, setCandidatosSumar] = useState([]);
    const [cargandoCandidatos, setCargandoCandidatos] = useState(false);
    const [seleccionSumar, setSeleccionSumar] = useState([]);

    const fetchProveedores = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/taxi-requests/proveedores');
            if (res.data.success) setProveedores(res.data.proveedores);
        } catch { }
    }, []);

    const fetchSolicitudes = useCallback(async () => {
        if (activeTab === 'reporte') return;
        try {
            setLoading(true);
            const params = {};
            if (activeTab !== 'history') {
                params.estado = activeTab;
            }
            if (filterDate) {
                params.fecha = filterDate;
            }
            const response = await axiosInstance.get('/api/taxi-requests/todas', { params });
            if (response.data.success) {
                let data = response.data.solicitudes;
                if (activeTab === 'history') {
                    data = data.filter(s => ['completed', 'cancelled'].includes(s.status));
                }
                setSolicitudes(data);
            }
        } catch (error) {
            console.error('Error fetching solicitudes:', error);
        } finally {
            setLoading(false);
        }
    }, [activeTab, filterDate]);

    useEffect(() => { fetchProveedores(); }, [fetchProveedores]);
    useEffect(() => { fetchSolicitudes(); }, [fetchSolicitudes]);

    // Fetch sugerencias de merge cuando hay pendientes
    useEffect(() => {
        if (activeTab !== 'pending') return;
        axiosInstance.get('/api/taxi-requests/sugerencias-merge')
            .then(res => setMergeSugerencias(res.data.grupos || []))
            .catch(() => setMergeSugerencias([]));
    }, [activeTab, solicitudes]);

    const handleUpdateSolicitud = async (id, updateData) => {
        try {
            setUpdating(id);
            const response = await axiosInstance.put(`/api/taxi-requests/${id}`, updateData);
            if (response.data.success) {
                fetchSolicitudes();
                setAssignModal(null);
                setConfirmModal(null);
            }
        } catch (error) {
            console.error('Error updating solicitud:', error);
        } finally {
            setUpdating(null);
        }
    };

    const handleCancel = async (id) => {
        try {
            setUpdating(id);
            await axiosInstance.delete(`/api/taxi-requests/${id}`);
            fetchSolicitudes();
        } catch (error) {
            console.error('Error cancelling solicitud:', error);
        } finally {
            setUpdating(null);
        }
    };

    // ─── Asignar proveedor + mailto ─────────────────────────────
    const openAssignModal = (solicitud) => {
        setAssignModal(solicitud);
        setSelectedProveedor(solicitud.proveedor_id || null);
    };

    const buildMailto = (solicitud, proveedor) => {
        const fecha = solicitud.date
            ? new Date(solicitud.date + 'T00:00:00').toLocaleDateString('es-VE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
            : '';
        const to12h = (t) => {
            if (!t) return '';
            const [h, m] = t.split(':').map(Number);
            const suffix = h >= 12 ? 'PM' : 'AM';
            const h12 = h % 12 || 12;
            return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
        };
        const hora = to12h(solicitud.time);

        const subject = `[SURA de Venezuela] Taxi ${fecha} ${hora} - ${solicitud.empleado}`;

        // Saludo según hora del día
        const horaActual = new Date().getHours();
        const saludo = horaActual < 12 ? 'Buenos dias' : horaActual < 18 ? 'Buenas tardes' : 'Buenas noches';

        const lines = [
            `${saludo} estimados ${proveedor.nombre},`,
            '',
            'Requiero servicio de taxi con los siguientes datos:',
            '',
            `Fecha:    ${fecha}`,
            `Hora:     ${hora}`,
        ];

        // Retorno
        if (solicitud.retorno) {
            lines.push(`Retorno:  ${to12h(solicitud.retorno.time)} (mismo dia, ruta inversa)`);
        }

        lines.push(
            `Origen:   ${solicitud.origin}`,
            `Destino:  ${solicitud.destination}`,
            `Vehiculo: ${solicitud.vehicleType === 'van' ? 'Van' : 'Carro'}`,
        );

        // Pasajeros — solicitante + adicionales, marcando externos
        const extras = (solicitud.passengerNames || [])
            .map(p => (typeof p === 'string' ? { nombre: p, esEmpleado: false } : p))
            .filter(p => p && p.nombre);
        lines.push(`Pasajeros: ${solicitud.passengers}`);
        lines.push(`  1. ${solicitud.empleado}`);
        extras.forEach((p, i) => lines.push(`  ${i + 2}. ${p.nombre}${p.esEmpleado ? '' : ' (externo)'}`));

        // Contacto
        if (solicitud.phone) {
            lines.push(`Contacto: ${solicitud.phone}`);
        }

        // Centro de costo
        if (solicitud.centro_costo) {
            lines.push(`Centro Costo: ${solicitud.centro_costo}`);
        }

        // Instrucciones adicionales
        if (solicitud.notes) {
            lines.push(`Instrucciones: ${solicitud.notes}`);
        }

        // Viaje compartido: un solo taxi para todas las personas del grupo
        if (solicitud.grupo_id) {
            const companeros = solicitud.companeros
                || solicitudes.filter(s => s.grupo_id === solicitud.grupo_id && s.id !== solicitud.id)
                    .map(gs => ({ nombre: gs.empleado, origin: gs.origin, destination: gs.destination, time: gs.time, passengers: gs.passengers }));
            if (companeros.length > 0) {
                lines.push('');
                lines.push('SERVICIO COMPARTIDO: en este mismo taxi van tambien:');
                companeros.forEach(gs => {
                    lines.push(`  - ${gs.nombre} (${gs.passengers || 1} pas.): ${gs.origin} > ${gs.destination}, ${to12h(gs.time)}`);
                });
            }
        }

        // Ruta
        if (solicitud.mapsUrl) {
            lines.push('', `Ruta: ${solicitud.mapsUrl}`);
        }

        lines.push(
            '',
            '*** POR FAVOR RESPONDER A ESTE CORREO con la confirmacion de disponibilidad y datos del conductor asignado (nombre, telefono y modelo del vehiculo). ***',
            '',
            'Saludos,',
            'Recepcion, SURA de Venezuela'
        );

        return `mailto:${proveedor.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`;
    };

    const handleAssign = async () => {
        if (!selectedProveedor || !assignModal) return;
        const proveedor = proveedores.find(p => p.id === selectedProveedor);

        // Guardar proveedor + confirmar automáticamente
        await handleUpdateSolicitud(assignModal.id, {
            estado: 'confirmed',
            proveedor_id: selectedProveedor,
        });

        // Abrir mailto: en nueva pestaña
        if (proveedor) {
            const updatedSolicitud = { ...assignModal, proveedor: proveedor.nombre };
            const a = document.createElement('a');
            a.href = buildMailto(updatedSolicitud, proveedor);
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.click();
            // Sellar que ya se disparó el correo al proveedor (apaga el aviso de "falta enviar")
            try { await axiosInstance.put(`/api/taxi-requests/${assignModal.id}`, { correo_proveedor_enviado: true }); } catch { }
            fetchSolicitudes();
        }
    };

    // Reenviar (o enviar) el correo al proveedor desde la tarjeta, y sellarlo
    const resendProveedorEmail = async (solicitud) => {
        const proveedor = proveedores.find(p => p.id === solicitud.proveedor_id);
        if (!proveedor) return;
        const a = document.createElement('a');
        a.href = buildMailto(solicitud, proveedor);
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
        try { await axiosInstance.put(`/api/taxi-requests/${solicitud.id}`, { correo_proveedor_enviado: true }); } catch { }
        fetchSolicitudes();
    };

    // ─── Confirmar con datos de conductor ───────────────────────
    const openConfirmModal = (solicitud) => {
        setConfirmModal(solicitud);
        setConductorInput(solicitud.driver || '');
        setConductorTelInput(solicitud.driverPhone || '');
        setConductorPlacaInput(solicitud.driverPlate || '');
    };

    const handleConfirm = () => {
        if (!confirmModal) return;
        const data = {};
        if (conductorInput.trim()) data.conductor = conductorInput.trim();
        if (conductorTelInput.trim()) data.conductor_telefono = conductorTelInput.trim();
        if (conductorPlacaInput.trim()) data.conductor_placa = conductorPlacaInput.trim();
        if (Object.keys(data).length === 0) return;
        handleUpdateSolicitud(confirmModal.id, data);
    };

    // Consolidación
    const toggleForGroup = (id) => {
        setSelectedForGroup(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleConsolidar = async () => {
        if (selectedForGroup.length < 2) return;
        try {
            setUpdating('consolidar');
            const res = await axiosInstance.post('/api/taxi-requests/consolidar', { solicitud_ids: selectedForGroup });
            if (res.data?.aviso) toast(res.data.aviso);
            fetchSolicitudes();
            setSelectedForGroup([]);
            setConsolidando(false);
        } catch (error) {
            toast.error(error.response?.data?.error || 'No se pudieron agrupar los viajes');
        } finally {
            setUpdating(null);
        }
    };

    const abrirSumar = async (base, miembros) => {
        setSumarModal({ base, miembros });
        setSeleccionSumar([]);
        setCandidatosSumar([]);
        setCargandoCandidatos(true);
        try {
            const idsViaje = new Set(miembros.map(m => m.id));
            const [pend, conf] = await Promise.all(['pending', 'confirmed'].map(estado =>
                axiosInstance.get('/api/taxi-requests/todas', { params: { estado, fecha: base.date } })
            ));
            const lista = [...(pend.data.solicitudes || []), ...(conf.data.solicitudes || [])]
                // Solo viajes sueltos del mismo dia (los de otro viaje compartido se separan primero).
                .filter(s => !idsViaje.has(s.id) && !s.grupo_id)
                .map(s => {
                    // Diferencia contra TODOS los del viaje: el vehiculo pasa a buscar a todos.
                    const difMin = Math.max(...miembros.map(m => {
                        const a = minutos(m.time), b = minutos(s.time);
                        return a == null || b == null ? 0 : Math.abs(a - b);
                    }));
                    return {
                        ...s,
                        _km: distanciaKm(base.destinationCoords, s.destinationCoords),
                        _difMin: difMin,
                        _horaOk: difMin <= VENTANA_RECOGIDA_MIN,
                    };
                })
                // Primero los que coinciden en horario, y dentro de eso los mas cercanos.
                .sort((a, b) => (b._horaOk - a._horaOk) || ((a._km ?? 999) - (b._km ?? 999)));
            setCandidatosSumar(lista);
        } catch (error) {
            toast.error(error.response?.data?.error || 'No se pudieron cargar las solicitudes');
        } finally {
            setCargandoCandidatos(false);
        }
    };

    const handleSumar = async () => {
        if (!sumarModal || seleccionSumar.length === 0) return;
        try {
            setUpdating('sumar');
            const res = await axiosInstance.post('/api/taxi-requests/consolidar', {
                solicitud_ids: [sumarModal.base.id, ...seleccionSumar],
                base_id: sumarModal.base.id,
            });
            toast.success(res.data?.aviso || 'Se sumaron al viaje');
            setSumarModal(null);
            fetchSolicitudes();
        } catch (error) {
            toast.error(error.response?.data?.error || 'No se pudieron sumar al viaje');
        } finally {
            setUpdating(null);
        }
    };

    const handleDesconsolidar = async (solicitudId) => {
        try {
            setUpdating(solicitudId);
            await axiosInstance.post(`/api/taxi-requests/desconsolidar/${solicitudId}`);
            fetchSolicitudes();
        } catch (error) {
            toast.error(error.response?.data?.error || 'No se pudo separar el viaje');
        } finally {
            setUpdating(null);
        }
    };

    const fetchReporte = useCallback(async () => {
        try {
            setLoadingReporte(true);
            const res = await axiosInstance.get('/api/taxi-requests/reporte', {
                params: { mes: reporteMes, anio: reporteAnio }
            });
            if (res.data.success) setReporte(res.data);
        } catch (error) {
            console.error('Error fetching reporte:', error);
        } finally {
            setLoadingReporte(false);
        }
    }, [reporteMes, reporteAnio]);

    useEffect(() => {
        if (activeTab === 'reporte') fetchReporte();
    }, [activeTab, fetchReporte]);

    const handleExportExcel = async () => {
        try {
            setExportingExcel(true);
            const res = await axiosInstance.get('/api/taxi-requests/reporte/excel', {
                params: { mes: reporteMes, anio: reporteAnio },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            const meses = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
            a.href = url;
            a.download = `Reporte_Taxis_${meses[reporteMes]}_${reporteAnio}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting Excel:', error);
        } finally {
            setExportingExcel(false);
        }
    };

    const filteredSolicitudes = solicitudes.filter(s => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            s.empleado?.toLowerCase().includes(term) ||
            s.origin?.toLowerCase().includes(term) ||
            s.destination?.toLowerCase().includes(term) ||
            s.driver?.toLowerCase().includes(term) ||
            s.proveedor?.toLowerCase().includes(term) ||
            s.centro_costo?.toLowerCase().includes(term)
        );
    });

    // Las solicitudes de un mismo viaje compartido se muestran juntas, en la
    // posicion de la primera del grupo.
    const posicionGrupo = {};
    filteredSolicitudes.forEach((s, i) => {
        if (s.grupo_id && posicionGrupo[s.grupo_id] === undefined) posicionGrupo[s.grupo_id] = i;
    });
    const solicitudesOrdenadas = filteredSolicitudes
        .map((s, i) => ({ s, i, clave: s.grupo_id ? posicionGrupo[s.grupo_id] : i }))
        .sort((a, b) => a.clave - b.clave || a.i - b.i)
        .map(x => x.s);

    const hasActions = (status) => ['pending', 'confirmed'].includes(status);

    // Tarjeta de una solicitud. Suelta, o dentro de la burbuja de un viaje compartido
    // (enGrupo), donde asignar y conductor van una sola vez para todo el viaje.
    const renderTarjeta = (solicitud, enGrupo = false) => {
        const statusConf = STATUS_CONFIG[solicitud.status] || STATUS_CONFIG.pending;
        const showActions = hasActions(solicitud.status);
        const isInGroup = !!solicitud.grupo_id && !enGrupo;
        const isSelectedForGroup = selectedForGroup.includes(solicitud.id);

        return (
            <motion.div
                key={solicitud.id}
                layout="position"
                layoutId={enGrupo ? undefined : idVuelo(solicitud.id)}
                transition={VUELO}
                initial={enGrupo ? false : { opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={enGrupo ? undefined : { opacity: 0, scale: 0.95 }}
                onClick={consolidando ? () => toggleForGroup(solicitud.id) : undefined}
                className={`bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all overflow-hidden ${
                    consolidando ? 'cursor-pointer' : ''
                } ${isSelectedForGroup ? 'border-indigo-400 ring-2 ring-indigo-400/30' : isInGroup ? 'border-indigo-200' : enGrupo ? 'border-indigo-100' : 'border-gray-100'}`}
            >
                {/* Grupo badge */}
                {isInGroup && (
                    <div className="bg-indigo-50 px-4 py-1.5 flex items-center justify-between">
                        <span className="text-xs font-medium text-indigo-600 flex items-center gap-1.5">
                            <Link2 size={12} className="shrink-0" />
                            <span className="truncate">
                                Viaje compartido con {(solicitud.companeros || []).map(c => c.nombre).filter(Boolean).join(', ') || `${(grupoMap[solicitud.grupo_id]?.length || 1) - 1} persona(s)`}
                            </span>
                        </span>
                        <button
                            onClick={(e) => { e.stopPropagation(); handleDesconsolidar(solicitud.id); }}
                            className="text-xs text-indigo-400 hover:text-indigo-600 flex items-center gap-1 transition-colors"
                        >
                            <Unlink size={12} />
                            Separar
                        </button>
                    </div>
                )}

                {/* Card Header */}
                <div className="p-4 pb-3">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                            {consolidando && (
                                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${isSelectedForGroup ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                                    {isSelectedForGroup && <Check size={12} className="text-white" />}
                                </div>
                            )}
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                <User size={16} className="text-gray-500" />
                            </div>
                            <div>
                                <p className="font-semibold text-gray-900 text-sm">{solicitud.empleado || 'Sin nombre'}</p>
                                {solicitud.phone && (
                                    <p className="text-xs text-gray-400 flex items-center gap-1">
                                        <Phone size={10} />
                                        {solicitud.phone}
                                    </p>
                                )}
                            </div>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusConf.bg} ${statusConf.text}`}>
                            {statusConf.label}
                        </span>
                    </div>

                    {/* Route */}
                    <div className="space-y-1.5 mb-3">
                        <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0" />
                            <span className="text-sm text-gray-700 truncate">{solicitud.origin}</span>
                        </div>
                        <div className="ml-1 border-l-2 border-dashed border-gray-200 h-2" />
                        <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-blue-500 flex-shrink-0" />
                            <span className="text-sm text-gray-700 truncate">{solicitud.destination}</span>
                        </div>
                    </div>

                    {/* Ida y vuelta + detalles */}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1 font-medium text-gray-700">
                            <Clock size={12} />
                            Ida {hora12(solicitud.time)}
                        </span>
                        {solicitud.retorno && (
                            <span className="flex items-center gap-1 font-medium text-indigo-600">
                                <Repeat size={12} />
                                Vuelta {hora12(solicitud.retorno.time)}
                            </span>
                        )}
                        <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {solicitud.date ? new Date(solicitud.date + 'T00:00:00').toLocaleDateString('es-VE', { weekday: 'long', day: 'numeric', month: 'long' }) : '-'}
                        </span>
                        <span className="flex items-center gap-1">
                            <Users size={12} />
                            {solicitud.passengers} {solicitud.passengers === 1 ? 'pasajero' : 'pasajeros'}
                        </span>
                        <span className="flex items-center gap-1">
                            {solicitud.vehicleType === 'van' ? <Bus size={12} /> : <Car size={12} />}
                            {solicitud.vehicleType === 'van' ? 'Van' : 'Carro'}
                        </span>
                    </div>

                    {/* Passenger Names */}
                    {solicitud.passengerNames && solicitud.passengerNames.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                            {solicitud.passengerNames.map((p, i) => {
                                const nombre = typeof p === 'string' ? p : p?.nombre;
                                const esEmpleado = typeof p === 'string' ? false : p?.esEmpleado;
                                if (!nombre) return null;
                                return (
                                    <span key={i} className={`px-2 py-0.5 rounded-lg text-xs ${esEmpleado ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-700'}`}>
                                        {nombre}{esEmpleado ? '' : ' · externo'}
                                    </span>
                                );
                            })}
                        </div>
                    )}

                    {/* Centro de costo */}
                    {solicitud.centro_costo && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 mt-1.5">
                            <Building2 size={11} />
                            CC: {solicitud.centro_costo}
                        </span>
                    )}

                    {/* Notes */}
                    {solicitud.notes && (
                        <div className="mt-2 p-2 bg-amber-50 rounded-lg">
                            <p className="text-xs text-amber-700 flex items-start gap-1">
                                <MessageSquare size={12} className="mt-0.5 flex-shrink-0" />
                                {solicitud.notes}
                            </p>
                        </div>
                    )}

                    {/* Proveedor + Conductor */}
                    {solicitud.proveedor && (
                        <div className="mt-2 flex items-center gap-3 text-xs">
                            <span className="flex items-center gap-1.5 text-purple-600 font-medium">
                                <Truck size={12} />
                                {solicitud.proveedor}
                            </span>
                            {solicitud.driver && (
                                <span className="flex items-center gap-1 text-gray-500">
                                    <UserCheck size={12} />
                                    {solicitud.driver}
                                </span>
                            )}
                            {solicitud.driverPlate && (
                                <span className="text-gray-500">{solicitud.driverPlate}</span>
                            )}
                        </div>
                    )}

                    {/* Aviso: taxi confirmado sin correo enviado al proveedor */}
                    {solicitud.status === 'confirmed' && solicitud.proveedor && !solicitud.correoProveedorEnviado && (
                        <div className="mt-2 flex items-center justify-between gap-2 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                            <span className="flex items-center gap-1.5 text-[11px] font-medium text-amber-700">
                                <AlertTriangle size={12} />
                                Falta enviar el correo al proveedor
                            </span>
                            <button
                                type="button"
                                onClick={() => resendProveedorEmail(solicitud)}
                                className="text-[11px] font-semibold text-amber-800 underline hover:text-amber-900 whitespace-nowrap"
                            >
                                Enviar ahora
                            </button>
                        </div>
                    )}

                    {/* Calificación */}
                    {solicitud.calificacion && (
                        <div className="mt-2 flex items-center gap-2">
                            <StarRating value={solicitud.calificacion.estrellas} size={12} />
                            {solicitud.calificacion.comentario && (
                                <span className="text-xs text-gray-500 truncate">{solicitud.calificacion.comentario}</span>
                            )}
                        </div>
                    )}
                </div>

                {/* Actions */}
                {/* Dentro de la burbuja, asignar y conductor son del viaje: aqui solo
                    se saca o se cancela a esta persona. */}
                {!consolidando && showActions && enGrupo && (
                    <div className="border-t border-gray-100 px-3 py-2 flex items-center justify-end gap-3">
                        <button
                            onClick={() => handleDesconsolidar(solicitud.id)}
                            disabled={updating === solicitud.id}
                            className="text-xs text-indigo-500 hover:text-indigo-700 flex items-center gap-1 transition-colors disabled:opacity-50"
                        >
                            <Unlink size={12} />
                            Separar del viaje
                        </button>
                        <button
                            onClick={() => handleCancel(solicitud.id)}
                            disabled={updating === solicitud.id}
                            className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 transition-colors disabled:opacity-50"
                        >
                            <XCircle size={12} />
                            Cancelar
                        </button>
                    </div>
                )}
                {!consolidando && showActions && !enGrupo && (
                    <div className="border-t border-gray-100 p-3 flex items-center gap-2">
                        <button
                            onClick={() => openAssignModal(solicitud)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors"
                        >
                            <Truck size={16} />
                            {solicitud.proveedor || 'Asignar'}
                        </button>
                        {solicitud.status === 'confirmed' && (
                            <button
                                onClick={() => openConfirmModal(solicitud)}
                                disabled={updating === solicitud.id}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors disabled:opacity-50"
                            >
                                {updating === solicitud.id ? <Loader2 size={16} className="animate-spin" /> : <UserCheck size={16} />}
                                Conductor
                            </button>
                        )}
                        <button
                            onClick={() => abrirSumar(solicitud, [solicitud])}
                            title="Sumar otra persona a este viaje"
                            aria-label="Sumar persona"
                            className="shrink-0 flex items-center justify-center px-3 py-2.5 rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
                        >
                            <UserPlus size={16} />
                        </button>
                        <button
                            onClick={() => handleCancel(solicitud.id)}
                            disabled={updating === solicitud.id}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50"
                        >
                            {updating === solicitud.id ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />}
                            Cancelar
                        </button>
                    </div>
                )}
            </motion.div>
        );
    };

    const itemsTaxi = [];
    solicitudesOrdenadas.forEach(s => {
        const ultimo = itemsTaxi[itemsTaxi.length - 1];
        if (s.grupo_id && ultimo?.grupoId === s.grupo_id) ultimo.miembros.push(s);
        else itemsTaxi.push(s.grupo_id ? { grupoId: s.grupo_id, miembros: [s] } : { solicitud: s });
    });

    const renderBurbuja = ({ grupoId, miembros }) => {
        // Si por el filtro solo se ve una persona del viaje, va como tarjeta suelta.
        if (miembros.length < 2) return renderTarjeta(miembros[0]);
        const base = miembros[0];
        const showActions = hasActions(base.status);
        const abierta = burbujasAbiertas.includes(grupoId);
        const statusConf = STATUS_CONFIG[base.status] || STATUS_CONFIG.pending;
        const alternar = () => setBurbujasAbiertas(prev => (
            prev.includes(grupoId) ? prev.filter(g => g !== grupoId) : [...prev, grupoId]
        ));
        return (
            <motion.div
                key={`grupo-${grupoId}`}
                layout="position"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                className="rounded-2xl border-2 border-indigo-200 bg-indigo-50/60 shadow-sm overflow-hidden self-start"
            >
                {/* Cabecera: se toca para desplegar el detalle */}
                <button
                    type="button"
                    onClick={alternar}
                    className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-indigo-100/50 transition-colors"
                >
                    <span className="flex items-center gap-2 text-sm font-semibold text-indigo-700">
                        <Link2 size={15} />
                        Viaje compartido · <NumeroRodante valor={miembros.length} /> personas
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="flex items-center gap-1 text-[11px] text-indigo-500">
                            {base.vehicleType === 'van' ? <Bus size={12} /> : <Car size={12} />}
                            <NumeroRodante valor={miembros.reduce((t, m) => t + (m.passengers || 1), 0)} /> pas.
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusConf.bg} ${statusConf.text}`}>{statusConf.label}</span>
                        <ChevronDown size={16} className={`text-indigo-400 transition-transform ${abierta ? 'rotate-180' : ''}`} />
                    </span>
                </button>

                <AnimatePresence initial={false} mode="wait">
                {abierta ? (
                    // Detalle completo: la tarjeta de cada persona, una debajo de otra
                    <motion.div
                        key="detalle"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                        style={{ overflow: 'hidden' }}
                    >
                        <div className="px-3 pb-3 space-y-3">
                            {miembros.map(m => renderTarjeta(m, true))}
                        </div>
                    </motion.div>
                ) : (
                    // Resumen: una linea por persona
                    <motion.div
                        key="resumen"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                        style={{ overflow: 'hidden' }}
                    >
                    <div className="px-3 pb-1 space-y-1.5">
                        {miembros.map(m => (
                            <motion.div
                                key={m.id}
                                layout="position"
                                layoutId={idVuelo(m.id)}
                                transition={VUELO}
                                onClick={consolidando ? () => toggleForGroup(m.id) : alternar}
                                className={`flex items-center gap-2.5 bg-white rounded-xl px-3 py-2 border cursor-pointer ${selectedForGroup.includes(m.id) ? 'border-indigo-400 ring-2 ring-indigo-400/30' : 'border-indigo-100'}`}
                            >
                                <div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center shrink-0">
                                    <User size={14} className="text-gray-500" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 truncate">{m.empleado || 'Sin nombre'}</p>
                                    <p className="text-xs text-gray-500 truncate">{m.destination}</p>
                                </div>
                                <span className="flex items-center gap-1 text-xs font-medium text-gray-600 shrink-0">
                                    <Clock size={12} />
                                    {hora12(m.time)}
                                </span>
                            </motion.div>
                        ))}
                        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 pt-1 text-[11px] text-indigo-600">
                            {base.date && (
                                <span className="flex items-center gap-1">
                                    <Calendar size={11} />
                                    {new Date(base.date + 'T00:00:00').toLocaleDateString('es-VE', { weekday: 'short', day: 'numeric', month: 'short' })}
                                </span>
                            )}
                            {base.proveedor && <span className="flex items-center gap-1 font-medium text-purple-600"><Truck size={11} />{base.proveedor}</span>}
                            {base.driver && <span className="flex items-center gap-1"><UserCheck size={11} />{base.driver}</span>}
                            {base.driverPlate && <span>{base.driverPlate}</span>}
                            <span className="text-indigo-400">Toca para ver el detalle</span>
                        </p>
                    </div>
                    </motion.div>
                )}
                </AnimatePresence>

                {!consolidando && showActions && (
                    <div className="p-3 pt-2 flex items-center gap-2">
                        <button
                            onClick={() => openAssignModal(base)}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium rounded-xl bg-white text-purple-600 border border-purple-100 hover:bg-purple-50 transition-colors"
                        >
                            <Truck size={16} />
                            {base.proveedor || 'Asignar'}
                        </button>
                        {base.status === 'confirmed' && (
                            <button
                                onClick={() => openConfirmModal(base)}
                                disabled={updating === base.id}
                                className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium rounded-xl bg-white text-blue-600 border border-blue-100 hover:bg-blue-50 transition-colors disabled:opacity-50"
                            >
                                {updating === base.id ? <Loader2 size={16} className="animate-spin" /> : <UserCheck size={16} />}
                                Conductor
                            </button>
                        )}
                        <button
                            onClick={() => abrirSumar(base, miembros)}
                            title="Sumar otra persona a este viaje"
                            aria-label="Sumar persona"
                            className="shrink-0 flex items-center justify-center px-3 py-2.5 rounded-xl bg-white text-indigo-600 border border-indigo-100 hover:bg-indigo-50 transition-colors"
                        >
                            <UserPlus size={16} />
                        </button>
                    </div>
                )}
            </motion.div>
        );
    };


    // Agrupar solicitudes por grupo_id para mostrar badge visual
    const grupoMap = {};
    filteredSolicitudes.forEach(s => {
        if (s.grupo_id) {
            if (!grupoMap[s.grupo_id]) grupoMap[s.grupo_id] = [];
            grupoMap[s.grupo_id].push(s.id);
        }
    });

    return (
        <MotionConfig reducedMotion="user">
        <div className="animate-fadeIn pb-8">
            {/* Tabs */}
            <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                <div className="flex bg-gray-100 rounded-lg p-1">
                    {FLOW_TABS.map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => { setActiveTab(tab.key); setConsolidando(false); setSelectedForGroup([]); }}
                            className={`px-4 py-2 rounded-md font-medium transition-all ${
                                activeTab === tab.key
                                    ? 'bg-blue-600 text-white shadow'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            {tab.label}
                            {activeTab === tab.key && filteredSolicitudes.length > 0 && (
                                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${activeTab === tab.key ? 'bg-white/20 text-white' : `bg-${tab.color}-100 text-${tab.color}-700`}`}>
                                    {filteredSolicitudes.length}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    {activeTab === 'pending' && filteredSolicitudes.length >= 2 && (
                        <button
                            onClick={() => { setConsolidando(!consolidando); setSelectedForGroup([]); }}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                                consolidando ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                            }`}
                        >
                            <Link2 size={16} />
                            {consolidando ? 'Cancelar agrupación' : 'Agrupar viajes'}
                        </button>
                    )}
                    <div className="flex bg-gray-100 rounded-lg p-1">
                        {DATA_TABS.map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-4 py-2 rounded-md font-medium transition-all ${
                                    activeTab === tab.key
                                        ? 'bg-blue-600 text-white shadow'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Consolidación bar */}
            <AnimatePresence>
                {consolidando && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden mb-4"
                    >
                        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Link2 size={16} className="text-indigo-600" />
                                <span className="text-sm font-medium text-indigo-900">
                                    Selecciona las solicitudes a agrupar ({selectedForGroup.length} seleccionadas)
                                </span>
                            </div>
                            <button
                                onClick={handleConsolidar}
                                disabled={selectedForGroup.length < 2 || updating === 'consolidar'}
                                className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-40"
                            >
                                {updating === 'consolidar' ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
                                Consolidar {selectedForGroup.length >= 2 ? `(${selectedForGroup.length})` : ''}
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Filters */}
            {activeTab !== 'reporte' && (
            <div className="flex items-center gap-3 mb-6">
                <div className="relative flex-1 max-w-sm">
                    <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar por solicitante, origen, destino o proveedor..."
                        className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors"
                    />
                </div>
                <div className="relative">
                    <Calendar size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="date"
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                        className="pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors"
                    />
                </div>
                {filterDate && (
                    <button
                        onClick={() => setFilterDate('')}
                        className="px-3 py-3 text-sm text-gray-500 hover:text-gray-700 bg-white border border-gray-200 rounded-xl"
                    >
                        Limpiar fecha
                    </button>
                )}
            </div>
            )}

            {/* Reporte */}
            {activeTab === 'reporte' ? (
                <ReportePanel
                    mes={reporteMes}
                    anio={reporteAnio}
                    onMesChange={setReporteMes}
                    onAnioChange={setReporteAnio}
                    reporte={reporte}
                    loading={loadingReporte}
                    exporting={exportingExcel}
                    onExport={handleExportExcel}
                />
            ) : null}

            {/* Content - solicitudes */}
            {activeTab === 'reporte' ? null : loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 size={32} className="animate-spin text-gray-400" />
                </div>
            ) : filteredSolicitudes.length === 0 ? (
                <div className="text-center py-20">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Car size={32} className="text-gray-300" />
                    </div>
                    <p className="text-gray-500 text-lg">No hay solicitudes {activeTab !== 'history' ? STATUS_CONFIG[activeTab]?.label.toLowerCase() + 's' : 'en el historial'}</p>
                    <p className="text-gray-400 text-sm mt-1">Las solicitudes aparecerán aquí cuando los empleados las creen</p>
                </div>
            ) : (<>
                {/* Sugerencias de merge inteligente */}
                {activeTab === 'pending' && mergeSugerencias.length > 0 && (
                    <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-2xl">
                        <div className="flex items-center gap-2 mb-3">
                            <Users size={16} className="text-indigo-600" />
                            <p className="text-sm font-semibold text-indigo-900">
                                {mergeSugerencias.length} grupo{mergeSugerencias.length !== 1 ? 's' : ''} de viajes podrían compartir transporte
                            </p>
                        </div>
                        <div className="space-y-2">
                            {mergeSugerencias.map((grupo, gi) => (
                                <div key={gi} className="flex items-center justify-between bg-white rounded-xl p-3 border border-indigo-100">
                                    <div className="flex-1 min-w-0">
                                        {grupo.tipo === 'sumar' ? (
                                            <p className="text-xs text-indigo-700">
                                                {grupo.solicitudes.map((s, si) => (
                                                    <span key={s.id}>
                                                        {si > 0 && ' y '}
                                                        <strong>{s.empleado}</strong> ({hora12(s.time)} → {s.destination})
                                                    </span>
                                                ))}
                                                {' '}podría{grupo.solicitudes.length > 1 ? 'n' : ''} sumarse al viaje compartido de{' '}
                                                <strong>{grupo.existentes.map(e => e.empleado).join(' y ')}</strong>
                                            </p>
                                        ) : (
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {grupo.solicitudes.map((s, si) => (
                                                    <span key={s.id} className="text-xs text-indigo-700">
                                                        {si > 0 && <span className="text-indigo-300 mx-1">+</span>}
                                                        <strong>{s.empleado}</strong> ({hora12(s.time)} → {s.destination})
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <p className="text-[11px] text-indigo-500 mt-1">
                                            {grupo.fecha && `${new Date(grupo.fecha + 'T00:00:00').toLocaleDateString('es-VE', { weekday: 'short', day: 'numeric', month: 'short' })} · `}
                                            Llegadas a {grupo.distancia_km} km o menos entre sí
                                            {grupo.pasajeros ? ` · ${grupo.pasajeros} pasajeros en ${grupo.vehiculo === 'van' ? 'van' : 'carro'}` : ''}
                                        </p>
                                    </div>
                                    <button
                                        onClick={async () => {
                                            // Sumar: basta una persona del viaje existente para unir a todo el grupo.
                                            const ids = [
                                                ...(grupo.tipo === 'sumar' ? [grupo.existentes[0].id] : []),
                                                ...grupo.solicitudes.map(s => s.id),
                                            ];
                                            try {
                                                const res = await axiosInstance.post('/api/taxi-requests/consolidar', { solicitud_ids: ids });
                                                if (res.data?.aviso) toast(res.data.aviso);
                                                fetchSolicitudes();
                                                setMergeSugerencias(prev => prev.filter((_, i) => i !== gi));
                                            } catch (err) { toast.error(err.response?.data?.error || 'No se pudieron agrupar los viajes'); }
                                        }}
                                        className="ml-3 flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors shrink-0"
                                    >
                                        <Link2 size={12} />
                                        {grupo.tipo === 'sumar' ? 'Sumar al viaje' : 'Consolidar'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    <AnimatePresence mode="popLayout">
                        {itemsTaxi.map(item => (item.grupoId ? renderBurbuja(item) : renderTarjeta(item.solicitud)))}
                    </AnimatePresence>
                </div>
            </>)}

            {/* Modal Sumar persona al viaje */}
            <Modal isOpen={!!sumarModal} onClose={() => setSumarModal(null)} size="md">
                <Modal.Header onClose={() => setSumarModal(null)}>
                    <div className="flex items-center gap-2">
                        <UserPlus size={20} className="text-indigo-500" />
                        <span>Sumar persona al viaje</span>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {sumarModal && (() => {
                        const { base, miembros } = sumarModal;
                        const pasajerosViaje = miembros.reduce((t, m) => t + (m.passengers || 1), 0);
                        const elegidos = candidatosSumar.filter(c => seleccionSumar.includes(c.id));
                        const total = pasajerosViaje + elegidos.reduce((t, c) => t + (c.passengers || 1), 0);
                        const vehiculo = total > CAPACIDAD_VAN ? null
                            : (total > CAPACIDAD_CARRO || base.vehicleType === 'van' || elegidos.some(c => c.vehicleType === 'van')) ? 'van' : 'carro';
                        return (
                            <div className="space-y-4">
                                <div className="p-3 bg-indigo-50 rounded-xl text-sm">
                                    <p className="font-medium text-gray-900">{miembros.map(m => m.empleado).join(', ')}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {hora12(base.time)} → {base.destination} · {base.date ? new Date(base.date + 'T00:00:00').toLocaleDateString('es-VE', { weekday: 'short', day: 'numeric', month: 'short' }) : ''}
                                        {base.proveedor ? ` · ${base.proveedor}` : ''}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-2">Solicitudes del mismo día</p>
                                    {cargandoCandidatos ? (
                                        <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-indigo-500" /></div>
                                    ) : candidatosSumar.length === 0 ? (
                                        <p className="text-sm text-gray-400 py-4 text-center">No hay otras solicitudes pendientes o confirmadas ese día.</p>
                                    ) : (
                                        <div className="space-y-1.5 max-h-72 overflow-y-auto">
                                            {candidatosSumar.map(c => {
                                                const marcado = seleccionSumar.includes(c.id);
                                                return (
                                                    <button
                                                        key={c.id}
                                                        type="button"
                                                        disabled={!c._horaOk}
                                                        title={c._horaOk ? undefined : `La recogida difiere ${c._difMin} min (máximo ${VENTANA_RECOGIDA_MIN})`}
                                                        onClick={() => setSeleccionSumar(prev => (marcado ? prev.filter(x => x !== c.id) : [...prev, c.id]))}
                                                        className={`w-full flex items-center gap-3 p-2.5 rounded-xl border text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${marcado ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'}`}
                                                    >
                                                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${marcado ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                                                            {marcado && <Check size={12} className="text-white" />}
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-sm font-medium text-gray-900 truncate">{c.empleado}</p>
                                                            <p className="text-xs text-gray-500 truncate">{hora12(c.time)} → {c.destination}</p>
                                                            {!c._horaOk && (
                                                                <p className="text-[11px] text-red-500">Horario de recogida no coincide ({c._difMin} min de diferencia)</p>
                                                            )}
                                                        </div>
                                                        <div className="text-right shrink-0">
                                                            <p className={`text-xs font-medium ${c._km == null ? 'text-gray-400' : c._km <= 5 ? 'text-emerald-600' : 'text-amber-600'}`}>
                                                                {c._km == null ? 'sin ubicación' : `${c._km.toFixed(1)} km`}
                                                            </p>
                                                            <p className="text-[11px] text-gray-400">{c.passengers || 1} pas. · {c.status === 'confirmed' ? 'confirmado' : 'pendiente'}</p>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <p className="text-[11px] text-gray-400 mt-2">
                                        La recogida tiene que ser con {VENTANA_RECOGIDA_MIN} min o menos de diferencia. La distancia es entre los destinos; en verde, a 5 km o menos.
                                    </p>
                                </div>

                                <div className={`p-3 rounded-xl text-sm ${vehiculo ? 'bg-gray-50 text-gray-700' : 'bg-red-50 text-red-700'}`}>
                                    {vehiculo
                                        ? <>Quedarían <strong>{total} pasajeros</strong> en {vehiculo === 'van' ? 'van' : 'carro'}{vehiculo === 'van' && base.vehicleType !== 'van' ? ' (el viaje pasa a van)' : ''}.</>
                                        : <>Serían <strong>{total} pasajeros</strong>: no caben ni en una van (máximo {CAPACIDAD_VAN}).</>}
                                    {seleccionSumar.length > 0 && base.proveedor && (
                                        <span className="block text-xs text-gray-500 mt-1">Heredan {base.proveedor}{base.driver ? ` y el conductor ${base.driver}` : ''}. Recuerda avisarle al proveedor del pasajero extra.</span>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex items-center gap-3 w-full">
                        <button
                            onClick={() => setSumarModal(null)}
                            className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleSumar}
                            disabled={seleccionSumar.length === 0 || updating === 'sumar'}
                            className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {updating === 'sumar' ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
                            Sumar al viaje
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Modal Asignar Proveedor */}
            <Modal
                isOpen={!!assignModal}
                onClose={() => { setAssignModal(null); setSelectedProveedor(null); }}
                size="sm"
            >
                <Modal.Header onClose={() => { setAssignModal(null); setSelectedProveedor(null); }}>
                    <div className="flex items-center gap-2">
                        <Truck size={20} className="text-purple-500" />
                        <span>Asignar Proveedor</span>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {assignModal && (
                        <div className="space-y-4">
                            <div className="p-3 bg-gray-50 rounded-xl text-sm">
                                <p className="font-medium text-gray-900">{assignModal.empleado}</p>
                                <div className="flex items-center gap-2 mt-1 text-gray-500">
                                    <span className="truncate">{assignModal.origin}</span>
                                    <ArrowRight size={12} className="flex-shrink-0" />
                                    <span className="truncate">{assignModal.destination}</span>
                                </div>
                                <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                                    <span>{assignModal.date ? new Date(assignModal.date + 'T00:00:00').toLocaleDateString('es-VE', { weekday: 'short', day: 'numeric', month: 'short' }) : '-'}</span>
                                    <span>{hora12(assignModal.time)}</span>
                                    {assignModal.retorno && <span className="text-indigo-500">Vuelta {hora12(assignModal.retorno.time)}</span>}
                                </div>
                                {assignModal.grupo_id && (assignModal.companeros || []).length > 0 && (
                                    <p className="text-xs text-indigo-600 font-medium mt-2 flex items-center gap-1.5">
                                        <Link2 size={12} />
                                        Viaje compartido: se aplica también a {(assignModal.companeros || []).map(c => c.nombre).join(', ')}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Selecciona proveedor</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {proveedores.map(prov => (
                                        <button
                                            key={prov.id}
                                            type="button"
                                            onClick={() => setSelectedProveedor(prov.id)}
                                            className={`p-3 rounded-xl border-2 text-left transition-all ${
                                                selectedProveedor === prov.id
                                                    ? 'border-purple-500 bg-purple-50'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <p className="font-semibold text-gray-900 text-sm">{prov.nombre}</p>
                                            {prov.calificacion_promedio && (
                                                <div className="flex items-center gap-1 mt-1">
                                                    <StarRating value={Math.round(prov.calificacion_promedio)} size={10} />
                                                    <span className="text-[10px] text-gray-400">{prov.calificacion_promedio}</span>
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <p className="text-xs text-gray-400">
                                Al asignar se abrirá un correo pre-redactado con los datos del viaje para enviar al proveedor.
                            </p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex items-center gap-3 w-full">
                        <button
                            onClick={() => { setAssignModal(null); setSelectedProveedor(null); }}
                            className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleAssign}
                            disabled={!selectedProveedor || updating}
                            className="flex-1 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {updating ? <Loader2 size={16} className="animate-spin" /> : <Truck size={16} />}
                            Asignar
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Modal Confirmar con datos conductor */}
            <Modal
                isOpen={!!confirmModal}
                onClose={() => setConfirmModal(null)}
                size="sm"
            >
                <Modal.Header onClose={() => setConfirmModal(null)}>
                    <div className="flex items-center gap-2">
                        <UserCheck size={20} className="text-blue-500" />
                        <span>Datos del conductor</span>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {confirmModal && (
                        <div className="space-y-4">
                            <div className="p-3 bg-gray-50 rounded-xl text-sm">
                                <p className="font-medium text-gray-900">{confirmModal.empleado}</p>
                                <div className="flex items-center gap-2 mt-1 text-gray-500">
                                    <span className="truncate">{confirmModal.origin}</span>
                                    <ArrowRight size={12} className="flex-shrink-0" />
                                    <span className="truncate">{confirmModal.destination}</span>
                                </div>
                                {confirmModal.proveedor && (
                                    <p className="text-xs text-purple-600 font-medium mt-2">Proveedor: {confirmModal.proveedor}</p>
                                )}
                                {confirmModal.grupo_id && (confirmModal.companeros || []).length > 0 && (
                                    <p className="text-xs text-indigo-600 font-medium mt-2 flex items-center gap-1.5">
                                        <Link2 size={12} />
                                        Viaje compartido: se aplica también a {(confirmModal.companeros || []).map(c => c.nombre).join(', ')}
                                    </p>
                                )}
                            </div>

                            <div>
                                <p className="text-sm font-medium text-gray-700 mb-3">Datos del conductor (opcional)</p>
                                <div className="space-y-2">
                                    <input
                                        type="text"
                                        value={conductorInput}
                                        onChange={(e) => setConductorInput(e.target.value)}
                                        placeholder="Nombre del conductor"
                                        className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:border-blue-500 transition-all"
                                        autoFocus
                                    />
                                    <div className="grid grid-cols-2 gap-2">
                                        <input
                                            type="text"
                                            value={conductorTelInput}
                                            onChange={(e) => setConductorTelInput(e.target.value)}
                                            placeholder="Teléfono"
                                            className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:border-blue-500 transition-all"
                                        />
                                        <input
                                            type="text"
                                            value={conductorPlacaInput}
                                            onChange={(e) => setConductorPlacaInput(e.target.value)}
                                            placeholder="Modelo del vehículo"
                                            maxLength={20}
                                            className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:border-blue-500 transition-all"
                                        />
                                    </div>

                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex items-center gap-3 w-full">
                        <button
                            onClick={() => setConfirmModal(null)}
                            className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={updating}
                            className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {updating ? <Loader2 size={16} className="animate-spin" /> : <UserCheck size={16} />}
                            Guardar
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
        </MotionConfig>
    );
};

export default TaxiManagementView;
